import React, { useContext } from 'react'

import { useCachedData } from 'hooks/useCachedData'
import { usePollApi } from 'hooks/usePollApi'
import { Asset, AssetId } from 'types/Asset'
import { Loadable, catLoadables, mapLoadable, mkLoadable } from 'utils/loadable'

export type AssetsById = { [k: number]: Asset }
export type AssetsByTicker = { [k: string]: Asset }

export type AssetsContext = {
  assets: Asset[]
  assetsById: AssetsById
  assetsByTicker: AssetsByTicker
}

const AssetsContext = React.createContext<Loadable<AssetsContext>>({
  loading: true,
})

function AssetsProviderInner({
  children,
}: {
  children: React.ReactNode
}): React.ReactElement {
  const assets = useCachedData(
    'contexts:Assets',
    // This is fairly useless, because we're missing e.g. swaps we hold
    // positions in etc. We could improve this significantly by supporting a
    // join from the assets endpoint to the asset_volumes table and ordering the
    // API query by that instead.
    mkLoadable(usePollApi<Asset>('assets', 60000)),
    assets =>
      assets
        .filter(
          asset =>
            asset.ticker === 'USD' ||
            bigCoins.includes(asset.ticker.toLowerCase()),
        )
        .slice(0, 500),
  )

  const assetsSorted = mapLoadable(assets, data =>
    data.sort((a, b) => a.ticker.localeCompare(b.ticker)),
  )

  if (assets.loading) {
    return (
      <AssetsContext.Provider value={{ loading: true }}>
        {children}
      </AssetsContext.Provider>
    )
  }

  const assetsById = mapLoadable<AssetsById, Asset[]>(assetsSorted, data =>
    Object.fromEntries(data.map(a => [a.id, a])),
  )
  const assetsByTicker = mapLoadable<AssetsByTicker, Asset[]>(
    assetsSorted,
    data => Object.fromEntries(data.map(a => [a.ticker, a])),
  )
  const activeAssets = mapLoadable(assets, data => data.filter(x => !x.expired))

  const value = catLoadables({
    assets: activeAssets,
    assetsById,
    assetsByTicker,
  })
  return (
    <AssetsContext.Provider value={value}>{children}</AssetsContext.Provider>
  )
}

export const AssetsProvider = React.memo(AssetsProviderInner)

// `asset` is one of:
//   - A number id
//   - A string ticker
//   - An object { id: number }
//   - An object { ticker: string }
export const useAsset = (
  asset: AssetId | undefined | null,
): Loadable<Asset | undefined> =>
  mapLoadable(useContext(AssetsContext), data => {
    if (asset === null || asset === undefined) {
      return undefined
    } else if (typeof asset === 'number') {
      return data.assetsById[asset]
    } else if (typeof asset === 'string') {
      return data.assetsByTicker[asset]
    } else if ('id' in asset) {
      return data.assetsById[asset.id]
    } else {
      return data.assetsByTicker[asset.ticker]
    }
  })

export const useAssets = (assets?: AssetId[]): Loadable<Asset[]> =>
  mapLoadable(useContext(AssetsContext), data => {
    if (!assets) {
      return data.assets
    }
    return assets
      .map(asset => {
        if (typeof asset === 'number') {
          return data.assetsById[asset]
        } else if (typeof asset === 'string') {
          return data.assetsByTicker[asset]
        } else if ('id' in asset) {
          return data.assetsById[asset.id]
        } else {
          return data.assetsByTicker[asset.ticker]
        }
      })
      .sort((a, b) => a.ticker.localeCompare(b.ticker))
      .filter(a => a)
  })

export const useAssetsById = (): Loadable<AssetsById> =>
  mapLoadable(useContext(AssetsContext), data => data.assetsById)

export function assetIdToString(id: AssetId): string {
  switch (typeof id) {
    case 'number':
    case 'string':
      return id.toString()
    default:
      return 'ticker' in id ? id.ticker : id.id.toString()
  }
}

// Top 100 coins on coinmarketcap.com as at 2025-05-02, ordered by market cap
// TODO: we should infer this from the database, and expose it in a sortable way
// via the API
export const bigCoins = [
  'BTC',
  'ETH',
  'USDT',
  'XRP',
  'BNB',
  'SOL',
  'USDC',
  'DOGE',
  'ADA',
  'TRX',
  'SUI',
  'LINK',
  'AVAX',
  'XLM',
  'LEO',
  'SHIB',
  'TON',
  'HBAR',
  'BCH',
  'HYPE',
  'LTC',
  'DOT',
  'DAI',
  'XMR',
  'BGB',
  'USDe',
  'PI',
  'PEPE',
  'APT',
  'UNI',
  'TAO',
  'NEAR',
  'OKB',
  'ONDO',
  'GT',
  'ICP',
  'AAVE',
  'ETC',
  'TRUMP',
  'KAS',
  'POL',
  'MNT',
  'CRO',
  'RENDER',
  'VET',
  'USD1',
  'FIL',
  'ALGO',
  'ATOM',
  'ENA',
  'FET',
  'TIA',
  'ARB',
  'S',
  'FDUSD',
  'BONK',
  'WLD',
  'KCS',
  'JUP',
  'MKR',
  'STX',
  'XDC',
  'FARTCOIN',
  'OP',
  'VIRTUAL',
  'IMX',
  'EOS',
  'SEI',
  'FLR',
  'IP',
  'DEXE',
  'INJ',
  'QNT',
  'GRT',
  'CRV',
  'PYUSD',
  'RAY',
  'JASMY',
  'FORM',
  'FLOKI',
  'WAL',
  'IOTA',
  'XAUt',
  'NEXO',
  'BSV',
  'CORE',
  'PAXG',
  'LDO',
  'SAND',
  'GALA',
  'THETA',
  'KAIA',
  'HNT',
  'BTT',
  'ENS',
  'PENGU',
  'CAKE',
  'BRETT',
  'FLOW',
  'MANA',
].map(a => a.toLowerCase())
